import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Users,
  FileText,
  Shield,
  Clock,
  Building2,
  Download,
  Handshake,
  Award,
} from 'lucide-react';

const benefits = [
  {
    icon: Users,
    title: 'Direkte Mandantenanbindung',
    description: 'Greifen Sie direkt auf die Buchhaltungsdaten Ihrer Mandanten zu – in Echtzeit.',
  },
  {
    icon: FileText,
    title: 'DATEV-Export',
    description: 'Ein-Klick-Export im DATEV-Format. Keine Schnittstellenprobleme mehr.',
  },
  {
    icon: Clock,
    title: 'Zeit sparen',
    description: 'Reduzieren Sie Rückfragen und Nacharbeiten durch vorbereitete Buchhaltung.',
  },
  {
    icon: Shield,
    title: 'GoBD-konform',
    description: 'Alle Buchungen sind revisionssicher und entsprechen den GoBD-Anforderungen.',
  },
  {
    icon: Building2,
    title: 'Multi-Mandanten',
    description: 'Verwalten Sie alle Mandanten in einem übersichtlichen Dashboard.',
  },
  {
    icon: Download,
    title: 'Automatische Belegübertragung',
    description: 'Belege werden automatisch kategorisiert und für Sie vorbereitet.',
  },
];

const partnerBenefits = [
  'Kostenloser Zugang zum Steuerberater-Portal',
  'Direkter Support-Kanal für Partner',
  'Schulungen und Webinare für Ihr Team',
  'Marketing-Materialien für Ihre Mandanten',
  'Provisionen für vermittelte Mandanten',
  'Früher Zugang zu neuen Features',
];

export default function Steuerberater(): ReactElement {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-secondary-900 to-secondary-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(14,165,233,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(14,165,233,0.1),transparent_50%)]" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-primary-500/20 text-primary-300 px-4 py-1.5 rounded-full text-sm font-medium mb-6 border border-primary-500/30">
                Für Steuerberater
              </span>
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Zusammenarbeit mit Mandanten neu gedacht
              </h1>
              <p className="text-lg text-secondary-300 mb-8">
                Saldify macht die Zusammenarbeit mit Ihren Mandanten effizienter.
                Direkter Zugriff auf vorbereitete Buchhaltungsdaten, automatische DATEV-Exporte
                und weniger Rückfragen.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#partner"
                  className="btn-primary px-6 py-3 shadow-lg"
                >
                  Partner werden
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="#demo"
                  className="border-2 border-white/30 text-white hover:bg-white/10 px-6 py-3 rounded-lg font-semibold transition-all inline-flex items-center justify-center gap-2"
                >
                  Demo anfordern
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <img src="/screenshots/app-buchhaltung.webp" alt="Buchhaltung in Saldify: Journal und Konten, die die Kanzlei direkt pruefen kann" className="w-full rounded-xl" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Ihre Vorteile als Steuerberater
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              Saldify entlastet Sie und Ihre Mitarbeiter bei der täglichen Arbeit mit Mandanten.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-secondary-50 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-secondary-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 lg:py-24 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              So funktioniert die Zusammenarbeit
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 text-center">
              <div className="w-12 h-12 bg-primary-600 text-white rounded-xl flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                Mandant nutzt Saldify
              </h3>
              <p className="text-secondary-600">
                Ihr Mandant erfasst Belege, erstellt Rechnungen und führt das Kassenbuch in Saldify.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center">
              <div className="w-12 h-12 bg-primary-600 text-white rounded-xl flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                Sie erhalten Zugriff
              </h3>
              <p className="text-secondary-600">
                Im Steuerberater-Portal sehen Sie alle Mandanten und deren vorbereitete Buchhaltung.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center">
              <div className="w-12 h-12 bg-primary-600 text-white rounded-xl flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                Export per Klick
              </h3>
              <p className="text-secondary-600">
                Exportieren Sie alle Daten im DATEV-Format direkt in Ihre Kanzleisoftware.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DATEV Integration */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-success-100 text-success-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                DATEV-Schnittstelle
              </span>
              <h2 className="text-3xl font-bold text-secondary-900 mb-6">
                Nahtlose Integration mit DATEV
              </h2>
              <p className="text-lg text-secondary-600 mb-6">
                Saldify exportiert alle Buchungen im DATEV-Format. Die Daten können direkt
                in DATEV Unternehmen online oder Ihre Kanzleisoftware importiert werden.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                  <span className="text-secondary-700">DATEV-Buchungsstapel Export</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                  <span className="text-secondary-700">Belegbilder im DATEV-Format</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                  <span className="text-secondary-700">Automatische Kontenzuordnung</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                  <span className="text-secondary-700">Kompatibel mit allen DATEV-Produkten</span>
                </li>
              </ul>
            </div>
            <div className="bg-secondary-50 rounded-2xl p-6">
              <img src="/screenshots/app-steuern.webp" alt="Steuerbereich in Saldify mit Auswertungen und Export fuer die Kanzlei" className="w-full rounded-xl" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Partner Program */}
      <section id="partner" className="py-16 lg:py-24 bg-gradient-to-br from-primary-600 to-primary-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Handshake className="w-8 h-8 text-primary-200" />
                <span className="text-primary-200 font-semibold">Partnerprogramm</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Werden Sie Saldify-Partner
              </h2>
              <p className="text-lg text-primary-100 mb-8">
                Als Saldify-Partner profitieren Sie von exklusiven Vorteilen und unterstützen
                Ihre Mandanten mit einer modernen Buchhaltungslösung.
              </p>
              <ul className="space-y-3 mb-8">
                {partnerBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary-200 flex-shrink-0 mt-0.5" />
                    <span className="text-white">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <Award className="w-8 h-8 text-primary-600" />
                <h3 className="text-xl font-bold text-secondary-900">Partner-Anmeldung</h3>
              </div>
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Kanzleiname
                  </label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Steuerberatung Mustermann"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Vorname
                    </label>
                    <input
                      type="text"
                      className="input"
                      placeholder="Max"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Nachname
                    </label>
                    <input
                      type="text"
                      className="input"
                      placeholder="Mustermann"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    E-Mail
                  </label>
                  <input
                    type="email"
                    className="input"
                    placeholder="max@stb-mustermann.de"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Telefon (optional)
                  </label>
                  <input
                    type="tel"
                    className="input"
                    placeholder="+49 123 456789"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Anzahl Mandanten
                  </label>
                  <select className="input">
                    <option>1-50</option>
                    <option>51-200</option>
                    <option>201-500</option>
                    <option>500+</option>
                  </select>
                </div>
                <button type="submit" className="btn-primary w-full py-3">
                  Partner werden
                </button>
                <p className="text-xs text-secondary-500 text-center">
                  Mit der Anmeldung akzeptieren Sie unsere{' '}
                  <Link to="/datenschutz" className="text-primary-600 hover:underline">
                    Datenschutzbestimmungen
                  </Link>
                  .
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-secondary-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-secondary-900 mb-4">
            Haben Sie Fragen?
          </h2>
          <p className="text-lg text-secondary-600 mb-8">
            Unser Team für Steuerberater-Partnerschaften berät Sie gerne persönlich.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+4912345678900"
              className="btn-primary px-8 py-4"
            >
              +49 123 456 789-00
            </a>
            <a
              href="mailto:partner@saldify.de"
              className="btn-outline px-8 py-4"
            >
              partner@saldify.de
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
